import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { Refresh, Home } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    const { user } = this.props;
    
    // Navigate to appropriate dashboard based on user type
    switch (user?.userType) {
      case 'customer':
        window.location.href = '/#/customer/dashboard';
        break;
      case 'pharmacy':
        window.location.href = '/#/pharmacy/dashboard';
        break;
      case 'rider':
        window.location.href = '/#/rider/dashboard';
        break;
      default:
        window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          gap={3}
          p={3}
        >
          <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" gutterBottom>
              An unexpected error occurred. This might be due to a user type mismatch or authentication issue.
            </Typography>
          </Alert>
          
          <Box textAlign="center">
            <Typography variant="body1" gutterBottom>
              Please try refreshing the page or logging out and logging back in.
            </Typography>
            
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Refresh />}
                onClick={this.handleRefresh}
              >
                Refresh Page
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Home />}
                onClick={this.handleGoHome}
              >
                Go to Dashboard
              </Button>
            </Box>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Wrapper component to provide context
const ErrorBoundaryWrapper = ({ children }) => {
  const { user } = useAuth();
  
  return (
    <ErrorBoundary user={user}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundaryWrapper;
